/**
 * @fileoverview Assignable-roles query hook.
 * @description Wraps GET /authorization/me/assignable-roles?purpose=INVITATION — the roles the
 * signed-in admin may put on an invitation (the Invite form's role picker), fetched only while the
 * form is open. Lives in shared, under the shared query key, so Add User, Invite User and
 * Reactivate User read one cached list (and one refresh reaches them all) instead of each keeping its own.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useQuery } from '@tanstack/react-query'

import { getAssignableRoles } from '@/shared/api/directory-options.service'
import { ASSIGNABLE_ROLES_QUERY_KEY } from '@/shared/constants/directory-options-query-keys.constants'

import type { AssignableRole } from '@/shared/types/directory-options.types'
import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Fetches the roles the signed-in admin may invite users into.
 * @param {boolean} enabled - Whether to fetch (the Invite form is open).
 * @returns {ReturnType<typeof useQuery>} The query object (data, isPending, ...).
 */
export function useAssignableRoles(enabled: boolean) {
  return useQuery<AssignableRole[], ApiError>({
    queryKey: ASSIGNABLE_ROLES_QUERY_KEY,
    queryFn: getAssignableRoles,
    enabled,
  })
}
